import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ChevronRight, ChevronLeft, Users, Calendar, Clock, 
  Package, Bell, Settings, CheckCircle, ArrowRight
} from 'lucide-react';

const welcomeSteps = [
  {
    id: 'welcome',
    title: 'Welcome to WorkForce Pro!',
    subtitle: 'Your 5-Day Free Trial Has Started',
    description: 'Thank you for signing up! Let us show you how to get the most out of your workforce management platform.',
    image: '/welcome-hero.png',
    icon: null,
    features: [
      'Unlimited employees during trial',
      'Full access to all features',
      'No credit card required'
    ]
  },
  {
    id: 'dashboard',
    title: 'Your Command Center',
    subtitle: 'Dashboard Overview',
    description: 'Your dashboard gives you a real-time view of your entire operation at a glance.',
    icon: Settings,
    features: [
      'Active Projects: See all ongoing projects',
      'Clocked In Now: Team members currently working',
      'This Week\'s Hours: Track labor costs',
      'Open Orders: Pending material requests'
    ],
    tip: 'Click on any stat card to see detailed information!'
  },
  {
    id: 'employees',
    title: 'Build Your Team',
    subtitle: 'Employee Management',
    description: 'Add your team members with just a few clicks. They\'ll receive login credentials automatically.',
    icon: Users,
    features: [
      'Add employees with name, email, and role',
      'Set hourly rates for accurate costing',
      'Assign to field-based or office roles',
      'Manage permissions by role'
    ],
    tip: 'Start by adding yourself as an employee to test the clock-in feature!'
  },
  {
    id: 'projects',
    title: 'Organize Your Work',
    subtitle: 'Project Management',
    description: 'Create projects for each job site or client. Assign teams and track progress.',
    icon: Package,
    features: [
      'Create unlimited projects',
      'Set locations and addresses',
      'Assign team members',
      'Track equipment usage'
    ],
    tip: 'Projects help you organize time tracking and generate accurate cost reports.'
  },
  {
    id: 'schedule',
    title: 'Plan Ahead',
    subtitle: 'Scheduling & Assignments',
    description: 'Create schedules for your team and send them daily assignment notifications.',
    icon: Calendar,
    features: [
      'Create daily/weekly schedules',
      'Assign employees to projects',
      'Send automatic shift reminders',
      'View in list, week, or calendar mode'
    ],
    tip: 'Use "Send Daily Assignments" to notify your team of their work for the day!'
  },
  {
    id: 'time',
    title: 'Track Every Hour',
    subtitle: 'Time Tracking',
    description: 'Employees clock in/out with GPS location. You see real-time attendance.',
    icon: Clock,
    features: [
      'One-tap clock in/out for employees',
      'GPS location verification',
      'Automatic hour calculations',
      'Export to Excel for payroll'
    ],
    tip: 'Time entries include location data to verify employees are at the right job site.'
  },
  {
    id: 'notifications',
    title: 'Stay Connected',
    subtitle: 'Push Notifications',
    description: 'Get instant updates about your team\'s activities and upcoming shifts.',
    icon: Bell,
    features: [
      'Shift reminders for employees',
      'Clock-in/out notifications',
      'Material order approvals',
      'Daily assignment alerts'
    ],
    tip: 'Enable notifications in your browser for the best experience!'
  },
  {
    id: 'ready',
    title: 'You\'re Ready!',
    subtitle: 'Start Managing Your Workforce',
    description: 'You now know the basics. Jump into your dashboard and start building your team!',
    icon: CheckCircle,
    features: [
      '1. Add your first employee',
      '2. Create a project',
      '3. Schedule a shift',
      '4. Try clocking in yourself'
    ],
    isLast: true
  }
];

export function WelcomeGuide({ onComplete, companyName }) {
  const [currentStep, setCurrentStep] = useState(0);
  const step = welcomeSteps[currentStep];
  const progress = ((currentStep + 1) / welcomeSteps.length) * 100;

  const handleNext = () => {
    if (currentStep < welcomeSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const Icon = step.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-1 bg-blue-400/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-blue-200 text-sm">
            <span>Step {currentStep + 1} of {welcomeSteps.length}</span>
            <button onClick={handleSkip} className="hover:text-white transition-colors">
              Skip Tour
            </button>
          </div>
        </div>

        <Card className="shadow-2xl border-0 overflow-hidden">
          <CardContent className="p-0">
            <div className="grid md:grid-cols-2">
              {/* Left side - Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                {Icon && (
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="h-8 w-8 text-blue-600" />
                  </div>
                )}
                
                <p className="text-blue-600 font-medium mb-2">{step.subtitle}</p>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h2>
                <p className="text-gray-600 mb-6">{step.description}</p>

                {step.features && (
                  <ul className="space-y-3 mb-6">
                    {step.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {step.tip && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <p className="text-amber-800 text-sm">
                      <strong>💡 Pro Tip:</strong> {step.tip}
                    </p>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-auto pt-4">
                  <Button
                    variant="ghost"
                    onClick={handlePrev}
                    disabled={currentStep === 0}
                    className="text-gray-500"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                  
                  <Button 
                    onClick={handleNext}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {step.isLast ? (
                      <>
                        Go to Dashboard
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Right side - Visual */}
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-8 md:p-12 flex items-center justify-center min-h-[400px]">
                <div className="text-center text-white">
                  {currentStep === 0 ? (
                    <div>
                      <div className="text-6xl mb-4">🎉</div>
                      <h3 className="text-2xl font-bold mb-2">Welcome, {companyName || 'Team'}!</h3>
                      <p className="text-blue-100">
                        Your workforce management journey starts now
                      </p>
                    </div>
                  ) : currentStep === welcomeSteps.length - 1 ? (
                    <div>
                      <div className="text-6xl mb-4">🚀</div>
                      <h3 className="text-2xl font-bold mb-2">Ready to Launch!</h3>
                      <p className="text-blue-100">
                        Your team is waiting for you
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Icon && <Icon className="h-24 w-24 mx-auto opacity-90" />}
                      <div className="grid grid-cols-3 gap-3 mt-8">
                        {[...Array(6)].map((_, i) => (
                          <div 
                            key={i}
                            className="h-16 bg-white/10 rounded-lg animate-pulse"
                            style={{ animationDelay: `${i * 0.1}s` }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {welcomeSteps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentStep(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentStep 
                  ? 'w-8 bg-white' 
                  : idx < currentStep 
                    ? 'bg-blue-300' 
                    : 'bg-blue-400/30'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default WelcomeGuide;
